import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Loader2, Eye, EyeOff, Pencil, X, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errText, imageUrl } from "@/lib/api";

const EMPTY = { title: "", subtitle: "", link: "", cta_label: "", active: true, order: 0 };

export const BannersTab = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const persistOrder = async (list) => {
    setRows(list);
    try {
      await Promise.all(list.map((b, idx) => api.put(`/banners/${b.banner_id}`, { ...b, order: idx })));
      load();
    } catch (err) {
      toast.error(errText(err));
      load();
    }
  };

  const move = (from, to) => {
    if (from == null || to == null || from === to || to < 0 || to >= rows.length) return;
    const next = [...rows];
    next.splice(to, 0, next.splice(from, 1)[0]);
    persistOrder(next);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/banners/all");
      setRows(data);
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data.file_id);
      }
      setImages((prev) => (editing ? ids.slice(0, 1) : [...prev, ...ids]));
      toast.success(`${ids.length} image${ids.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setImages([]);
    setEditing(null);
  };

  const startEdit = (b) => {
    setEditing(b.banner_id);
    setForm({
      title: b.title || "", subtitle: b.subtitle || "", link: b.link || "",
      cta_label: b.cta_label || "", active: b.active, order: b.order ?? 0,
    });
    setImages(b.image_id ? [b.image_id] : []);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Give the banner a headline");
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/banners/${editing}`, { ...form, order: Number(form.order || 0), image_id: images[0] || null });
        toast.success("Banner updated");
      } else {
        const slides = images.length ? images : [null];
        const base = Number(form.order || 0);
        await Promise.all(
          slides.map((image_id, i) =>
            api.post("/banners", { ...form, order: base + i, image_id })
          )
        );
        toast.success(`${slides.length} banner${slides.length > 1 ? "s" : ""} added`);
      }
      reset();
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (b) => {
    try {
      await api.put(`/banners/${b.banner_id}`, { ...b, active: !b.active });
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/banners/${b.banner_id}`);
      toast.success("Banner removed");
      if (editing === b.banner_id) reset();
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_360px]" data-testid="admin-banners">
      <div className="space-y-4">
        {rows.length === 0 ? (
          <div className="rounded-2xl border border-border p-10">
            <p className="font-display text-xl">No promo banners yet</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Until you add one, the home page shows the default highlights strip.
            </p>
          </div>
        ) : (
          rows.map((b, idx) => (
            <div
              key={b.banner_id}
              draggable
              onDragStart={() => setDragIndex(idx)}
              onDragEnter={() => setOverIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                move(dragIndex, idx);
                setDragIndex(null);
                setOverIndex(null);
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
              className={`flex flex-col sm:flex-row items-start gap-4 rounded-2xl border p-4 transition-colors duration-200 ${
                overIndex === idx && dragIndex !== idx
                  ? "border-primary"
                  : editing === b.banner_id
                    ? "border-primary"
                    : "border-border"
              } ${dragIndex === idx ? "opacity-40" : ""}`}
              data-testid={`banner-row-${b.banner_id}`}
            >
              <div className="flex items-center gap-1 sm:flex-col sm:pt-1">
                <span className="cursor-grab text-muted-foreground" title="Drag to reorder" data-testid={`banner-drag-${b.banner_id}`}>
                  <GripVertical className="h-5 w-5" />
                </span>
                <button
                  type="button"
                  onClick={() => move(idx, idx - 1)}
                  disabled={idx === 0}
                  className="rounded-md p-1 hover:bg-muted disabled:opacity-30 transition-colors duration-150"
                  aria-label="Move up"
                  data-testid={`banner-up-${b.banner_id}`}
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, idx + 1)}
                  disabled={idx === rows.length - 1}
                  className="rounded-md p-1 hover:bg-muted disabled:opacity-30 transition-colors duration-150"
                  aria-label="Move down"
                  data-testid={`banner-down-${b.banner_id}`}
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>
              {b.image_id ? (
                <img src={imageUrl(b.image_id)} alt={b.title} className="h-24 w-full sm:w-44 rounded-xl object-cover" />
              ) : (
                <div className="grid h-24 w-full sm:w-44 place-items-center rounded-xl bg-muted text-[12px] text-muted-foreground">
                  Text only
                </div>
              )}
              <div className="flex-1">
                <p className="font-display text-[15px]">{b.title}</p>
                {b.subtitle && <p className="mt-1 text-[13px] text-muted-foreground">{b.subtitle}</p>}
                <p className="mt-2 text-[12px] text-muted-foreground">
                  {b.active ? "Live on home page" : "Hidden"} · position {idx + 1}
                  {b.link ? ` · ${b.link}` : ""}
                </p>
              </div>
              <div className="flex flex-row gap-1 sm:flex-col">
                <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => startEdit(b)} data-testid={`banner-edit-${b.banner_id}`}>
                  <Pencil className="h-4 w-4 mr-1.5" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => toggle(b)} data-testid={`banner-toggle-${b.banner_id}`}>
                  {b.active ? <EyeOff className="h-4 w-4 mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
                  {b.active ? "Hide" : "Show"}
                </Button>
                <Button variant="ghost" size="sm" className="rounded-lg text-destructive" onClick={() => remove(b)} data-testid={`banner-delete-${b.banner_id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <form onSubmit={save} className="h-fit rounded-2xl border border-border p-6 space-y-3.5" data-testid="banner-form">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg">{editing ? "Edit banner" : "Add banners"}</h3>
          {editing && (
            <button type="button" onClick={reset} className="flex items-center gap-1 text-[12px] font-semibold underline" data-testid="banner-cancel-edit">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Headline</Label>
          <Input value={form.title} onChange={set("title")} placeholder="Monsoon offer — zero brokerage" className="h-12 rounded-lg" data-testid="banner-title-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Sub-text</Label>
          <Textarea value={form.subtitle} onChange={set("subtitle")} rows={2} className="rounded-lg" data-testid="banner-subtitle-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Button label (optional)</Label>
          <Input value={form.cta_label} onChange={set("cta_label")} placeholder="Explore homes" className="h-12 rounded-lg" data-testid="banner-cta-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Link (optional)</Label>
          <Input value={form.link} onChange={set("link")} placeholder="https://…" className="h-12 rounded-lg" data-testid="banner-link-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Order</Label>
          <Input type="number" value={form.order} onChange={set("order")} className="h-12 rounded-lg" data-testid="banner-order-input" />
          <p className="text-[11px] text-muted-foreground">Or just drag the banners on the left into the order you want.</p>
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border p-4 text-[13px] font-semibold hover:border-foreground/30 transition-colors duration-200">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {editing ? (images.length ? "Replace image" : "Upload image") : "Upload banner images"}
          <input type="file" accept="image/*" multiple={!editing} className="hidden" onChange={upload} data-testid="banner-image-input" />
        </label>
        {images.length > 0 && (
          <div className="grid grid-cols-3 gap-2" data-testid="banner-image-previews">
            {images.map((id) => (
              <div key={id} className="relative">
                <img src={imageUrl(id)} alt="Preview" className="h-20 w-full rounded-lg bg-muted object-contain" />
                <button
                  type="button"
                  onClick={() => setImages((p) => p.filter((x) => x !== id))}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-foreground p-1 text-background"
                  aria-label="Remove image"
                  data-testid={`banner-image-remove-${id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">
          {editing
            ? "Best results with a wide image around 1600 × 600 px — the full picture is shown, never cropped."
            : "Pick several images at once and we'll create one rotating banner per image. Wide images (~1600 × 600 px) look best."}
        </p>
        <Button type="submit" disabled={busy} className="w-full h-12 rounded-lg font-bold hover:brightness-95" data-testid="banner-save-btn">
          {busy ? "Saving…" : editing ? "Save changes" : images.length > 1 ? `Add ${images.length} banners` : "Add banner"}
        </Button>
      </form>
    </div>
  );
};

export default BannersTab;
